/**
 * Extract the applicable price from a credit pack catalog item
 * Returns amount_cents_sub if present (subscriber price), otherwise amount_cents_nosub
 */
export function extractPrice(item: any): number {
  console.log("[v0] extractPrice called with item:", {
    packKey: item.packKey,
    has_sub: !!item.amount_cents_sub,
    has_nosub: !!item.amount_cents_nosub,
    amount_cents_sub: item.amount_cents_sub,
    amount_cents_nosub: item.amount_cents_nosub,
  })

  const priceCents = item.amount_cents_sub ?? item.amount_cents_nosub ?? item.amount_cents ?? 0
  const priceInDollars = priceCents / 100

  console.log("[v0] extractPrice result:", {
    priceCents,
    priceInDollars,
    source: item.amount_cents_sub ? "subscriber" : item.amount_cents_nosub ? "non-subscriber" : "fallback",
  })

  return priceInDollars
}

/**
 * Determine if a subscription qualifies for subscriber pricing
 * Returns true if subscription is active and NOT meter_only
 */
export function hasActiveSubscription(subscription: any): boolean {
  if (!subscription) {
    console.log("[v0] hasActiveSubscription: no subscription provided")
    return false
  }

  const isActive = subscription.status === "active"
  const isNotMeterOnly = subscription.planKey !== "meter_only" && subscription.type !== "meter_only"
  const qualifies = isActive && isNotMeterOnly

  console.log("[v0] hasActiveSubscription:", {
    status: subscription.status,
    planKey: subscription.planKey,
    type: subscription.type,
    isActive,
    isNotMeterOnly,
    qualifies,
  })

  return qualifies
}

/**
 * Calculate savings percentage between subscriber and non-subscriber pricing
 * Returns percentage as a whole number (e.g., 15 for 15%)
 */
export function calculateSavingsPercent(subPriceCents: number, nosubPriceCents: number): number {
  if (!subPriceCents || !nosubPriceCents || nosubPriceCents <= subPriceCents) {
    console.log("[v0] calculateSavingsPercent: invalid prices or no savings", {
      subPriceCents,
      nosubPriceCents,
    })
    return 0
  }

  const savingsPercent = Math.round(((nosubPriceCents - subPriceCents) / nosubPriceCents) * 100)

  console.log("[v0] calculateSavingsPercent:", {
    subPriceCents,
    nosubPriceCents,
    savingsPercent,
  })

  return savingsPercent
}

/**
 * Get the appropriate pricing badge text based on which price field is present
 * Returns "Member Price" for subscriber pricing, null otherwise
 */
export function getPricingBadge(item: any): string | null {
  const badge = item.amount_cents_sub ? "Member Price" : null

  console.log("[v0] getPricingBadge:", {
    packKey: item.packKey,
    has_sub: !!item.amount_cents_sub,
    badge,
  })

  return badge
}

/**
 * Get upsell message for non-subscribers showing potential savings
 * Returns message with savings percentage, or null if not applicable
 */
export function getUpsellMessage(item: any, hasSubscription: boolean): string | null {
  // Don't show upsell if user already has subscription
  if (hasSubscription) {
    console.log("[v0] getUpsellMessage: user has subscription, no upsell")
    return null
  }

  // Only show upsell if both prices are available (mode=both)
  if (!item.amount_cents_sub || !item.amount_cents_nosub) {
    console.log("[v0] getUpsellMessage: missing price data for comparison", {
      has_sub: !!item.amount_cents_sub,
      has_nosub: !!item.amount_cents_nosub,
    })
    return null
  }

  const savingsPercent = calculateSavingsPercent(item.amount_cents_sub, item.amount_cents_nosub)

  if (savingsPercent <= 0) {
    console.log("[v0] getUpsellMessage: no savings to display")
    return null
  }

  const message = `Members save ${savingsPercent}%`

  console.log("[v0] getUpsellMessage:", {
    packKey: item.packKey,
    savingsPercent,
    message,
  })

  return message
}

/**
 * Format a price in dollars to currency string with 2 decimal places
 * Example: 60.5 -> "60.50", 100 -> "100.00"
 */
export function formatCurrency(priceInDollars: number): string {
  const formatted = priceInDollars.toFixed(2)
  console.log("[v0] formatCurrency:", {
    input: priceInDollars,
    output: formatted,
  })
  return formatted
}
